import type { JSX } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { revealUp, inViewOnce, useRevealGate } from './anim';
import { CreateRoomButton } from './CreateRoomButton';
import { CtaCritter } from './CtaCritter';

/**
 * The closing call — a near-full-screen, confident, emotional finish. One big headline and the big Create
 * Room button again, over a warm glow. The last thing a visitor sees should make them click.
 */
export function FinalCtaSection(): JSX.Element {
  // `reveal` gates the entrance rise (also true during SSG prerender / boot-over-prerender);
  // `reduce` is the device preference alone and keeps gating the ambient glow pulse.
  const reveal = useRevealGate();
  const reduce = useReducedMotion();
  return (
    <section id="final-cta" aria-labelledby="final-heading" className="relative flex min-h-[80svh] items-center overflow-hidden px-4 py-24 sm:px-6">
      {/* Warm glow behind the closing line. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute left-1/2 top-1/2 h-[70vh] w-[70vh] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-50 blur-3xl ${reduce ? '' : 'animate-pulse'}`}
          style={{ background: 'radial-gradient(circle, rgba(255,210,63,0.22), transparent 60%)' }}
        />
      </div>

      <motion.div
        variants={reveal ? undefined : revealUp}
        initial={reveal ? false : 'hidden'}
        whileInView={reveal ? undefined : 'show'}
        viewport={inViewOnce}
        className="relative mx-auto max-w-3xl text-center"
      >
        {/* The page closes on the REAL mascot, alive (P0-4): a live procedural duck idling, not a flat 🦆.
            Box reserved (no CLS); lazy + IO-gated + reduced-motion safe inside CtaCritter; emoji fallback
            on no-WebGL. */}
        <div aria-hidden="true" className="mx-auto h-28 w-28 sm:h-32 sm:w-32">
          <CtaCritter species="duck" color="yellow" activity="idle" scale={2.6} />
        </div>
        <h2 id="final-heading" className="mt-6 text-balance text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-6xl">
          Your people are going to love it here.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-pretty text-lg text-white/65 sm:text-xl">
          Open a room, send the link, and watch everyone wander in. The workday just got somewhere worth being.
        </p>
        <div className="mt-10 flex justify-center">
          <CreateRoomButton size="lg" pulse />
        </div>
      </motion.div>
    </section>
  );
}
